import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Camera, Loader2, Trash2, Link2, Unlink2, Search, AlertTriangle, Activity, HeartPulse, Award, ShieldAlert } from 'lucide-react';
import {
  adminCreatePlayerProfile, adminDeletePlayerProfile, adminDeletePlayerProfilePhoto,
  adminGetPlayerProfile, adminLinkUserToProfile, adminSearchUnlinkedPlayers,
  adminUnlinkUserFromProfile, adminUpdatePlayerProfile,
  adminUpdatePlayerProfileStatus, adminUpdatePlayerProfileVisibility,
  adminUploadPlayerProfilePhoto, fetchClubsBySport, fetchPositionsBySport,
  fetchSportsList, fetchTeamsByClub, friendlyError,
  fetchPlayerIntelligenceLatest,
} from '@/lib/api';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { ROUTES } from '@/lib/constants';
import { toast } from 'sonner';

const GENDERS = [
  { value: 'not_specified', label: 'No especificado' },
  { value: 'male', label: 'Masculino' }, { value: 'female', label: 'Femenino' },
];
const SIDES = [
  { value: 'not_specified', label: 'No especificado' },
  { value: 'right', label: 'Derecho' }, { value: 'left', label: 'Izquierdo' }, { value: 'both', label: 'Ambidiestro' },
];
const STATUSES = [
  { value: 'draft', label: 'Borrador' }, { value: 'pending_review', label: 'Pendiente revisión' },
  { value: 'approved', label: 'Aprobado' }, { value: 'hidden', label: 'Oculto' },
  { value: 'disabled', label: 'Desactivado' },
];
const SCOPES = [
  { value: 'none', label: 'Ninguna' },
  { value: 'team_card', label: 'Tarjeta de equipo' },
  { value: 'full_profile_and_highlights', label: 'Perfil completo + highlights' },
];

function emptyForm() {
  return {
    first_name: '', first_surname: '', second_surname: '',
    email: '',
    gender: 'not_specified', date_of_birth: '',
    height_cm: '', weight_kg: '', sport_id: '', club_id: '', team_id: '',
    position: '', dominant_side: 'not_specified', bio: '', user_id: '',
  };
}

export default function AdminPlayerProfileEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'nueva';
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  const [linkSelectedUser, setLinkSelectedUser] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0 cerrado, 1 aviso, 2 tecleo
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const sportsRes = await fetchSportsList();
      setSports(sportsRes.items || []);
      if (!isNew) {
        const p = await adminGetPlayerProfile(id);
        setProfile(p);
        // Fallback retro-compat: si el backend no devuelve apellidos separados
        // (ficha antigua), dividimos el legacy last_name aquí.
        let fs = p.first_surname || '';
        let ss = p.second_surname || '';
        if (!fs && p.last_name) {
          const tokens = String(p.last_name).trim().split(/\s+/);
          fs = tokens[0] || '';
          ss = tokens.slice(1).join(' ');
        }
        setForm({
          first_name: p.first_name || '',
          first_surname: fs, second_surname: ss,
          email: p.email || '',
          gender: p.gender || 'not_specified',
          date_of_birth: p.date_of_birth || '',
          height_cm: p.height_cm ?? '', weight_kg: p.weight_kg ?? '',
          sport_id: p.sport_id || '', club_id: p.club_id || '',
          team_id: p.team_id || '', position: p.position || '',
          dominant_side: p.dominant_side || 'not_specified',
          bio: p.bio || '', user_id: '',
        });
      }
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!form.sport_id) { setClubs([]); setTeams([]); setPositions([]); return; }
    (async () => {
      try {
        const [c, p] = await Promise.all([fetchClubsBySport(form.sport_id), fetchPositionsBySport(form.sport_id)]);
        setClubs(c.items || []); setPositions(p.items || []);
      } catch (err) { toast.error(friendlyError(err)); }
    })();
  }, [form.sport_id]);
  useEffect(() => {
    if (!form.club_id) { setTeams([]); return; }
    (async () => { try { const t = await fetchTeamsByClub(form.club_id); setTeams(t.items || []); } catch (err) { toast.error(friendlyError(err)); } })();
  }, [form.club_id]);

  const buildPayload = () => {
    const fs = form.first_surname.trim();
    const ss = form.second_surname.trim();
    const p = {
      first_name: form.first_name.trim(),
      first_surname: fs,
      // second_surname puede ir vacío para jugadores con un único apellido.
      second_surname: ss || null,
      // Mantenemos `last_name` como concat legacy para lectores antiguos.
      last_name: [fs, ss].filter(Boolean).join(' '),
      // Email opcional de contacto (usado para matching inverso). Se
      // envía en minúsculas o `null` si el admin lo deja vacío.
      email: form.email.trim().toLowerCase() || null,
      gender: form.gender, sport_id: form.sport_id, club_id: form.club_id,
      dominant_side: form.dominant_side,
    };
    if (form.date_of_birth) p.date_of_birth = form.date_of_birth;
    if (form.height_cm !== '') p.height_cm = Number(form.height_cm);
    if (form.weight_kg !== '') p.weight_kg = Number(form.weight_kg);
    if (form.team_id) p.team_id = form.team_id;
    if (form.position) p.position = form.position.trim();
    if (form.bio) p.bio = form.bio.trim();
    if (isNew && form.user_id) p.user_id = form.user_id;
    return p;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.first_surname.trim()) {
      toast.error('Nombre y primer apellido son obligatorios.');
      return;
    }
    if (!form.sport_id || !form.club_id) { toast.error('Selecciona deporte y club.'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await adminCreatePlayerProfile(buildPayload());
        toast.success(res.message || 'Ficha creada.');
        navigate(`/admin/fichas-jugadores/${res.data.id}`);
      } else {
        const res = await adminUpdatePlayerProfile(id, buildPayload());
        toast.success(res.message || 'Ficha actualizada.');
        await load();
      }
    } catch (err) { toast.error(friendlyError(err)); } finally { setSaving(false); }
  };

  const handleStatus = async (next) => {
    try { await adminUpdatePlayerProfileStatus(id, next); toast.success('Estado actualizado.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };

  const handleVisibility = async (field, value) => {
    try { await adminUpdatePlayerProfileVisibility(id, { [field]: value }); toast.success('Visibilidad actualizada.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };

  /**
   * Toggle único "Visibilidad pública del perfil".
   *
   * Encapsula la convención de NetScouters ya utilizada por el resto de
   * la aplicación, escribiendo EXCLUSIVAMENTE sobre los dos campos
   * existentes vía los endpoints existentes (sin nueva lógica ni nuevos
   * campos):
   *
   *   Visible  →  status = 'approved'
   *               external_visibility_scope = 'full_profile_and_highlights'
   *   Oculto   →  status = 'draft'
   *               external_visibility_scope = 'none'
   *
   * Los selectores avanzados (Estado + Visibilidad externa) siguen
   * disponibles debajo para casos que necesiten valores intermedios
   * (pending_review, hidden, team_card, etc.).
   */
  const isPubliclyVisible = (
    profile?.status === 'approved'
    && profile?.external_visibility_scope === 'full_profile_and_highlights'
  );
  const [publicToggleBusy, setPublicToggleBusy] = useState(false);
  const handlePublicVisibilityToggle = async (next) => {
    if (publicToggleBusy) return;
    setPublicToggleBusy(true);
    try {
      if (next) {
        await adminUpdatePlayerProfileStatus(id, 'approved');
        await adminUpdatePlayerProfileVisibility(id, {
          external_visibility_scope: 'full_profile_and_highlights',
        });
        toast.success('Perfil publicado públicamente.');
      } else {
        await adminUpdatePlayerProfileStatus(id, 'draft');
        await adminUpdatePlayerProfileVisibility(id, {
          external_visibility_scope: 'none',
        });
        toast.success('Perfil ocultado del público.');
      }
      await load();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setPublicToggleBusy(false);
    }
  };

  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { await adminUploadPlayerProfilePhoto(id, file); toast.success('Foto actualizada.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const onDeletePhoto = async () => {
    if (!window.confirm('¿Eliminar la foto?')) return;
    try { await adminDeletePlayerProfilePhoto(id); toast.success('Foto eliminada.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };

  const handleLinkSubmit = async () => {
    if (!linkSelectedUser?.id) {
      toast.error('Selecciona un usuario de la lista.');
      return;
    }
    setLinkDialogOpen(false);
    try {
      await adminLinkUserToProfile(id, linkSelectedUser.id);
      setLinkQuery(''); setLinkResults([]); setLinkSelectedUser(null);
      toast.success('Ficha vinculada.');
      await load();
    } catch (err) { toast.error(friendlyError(err)); }
  };
  const handleUnlink = async () => {
    if (!window.confirm('¿Desvincular el usuario? Se mantienen los datos deportivos.')) return;
    try { await adminUnlinkUserFromProfile(id); toast.success('Usuario desvinculado.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };

  // Eliminación definitiva (doble confirmación + tecleo del nombre)
  const fullName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
  const canDelete = deleteTyped.trim().toLowerCase() === fullName.toLowerCase() && fullName.length > 0;
  const handleDelete = async () => {
    if (!canDelete || deleting) return;
    setDeleting(true);
    try {
      await adminDeletePlayerProfile(id);
      toast.success('Ficha eliminada definitivamente.');
      navigate('/admin/fichas-jugadores');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setDeleting(false);
      setDeleteStep(0); setDeleteTyped('');
    }
  };

  // Autocomplete con debounce (300ms)
  useEffect(() => {
    if (!linkDialogOpen) return;
    const term = linkQuery.trim();
    if (term.length < 2) { setLinkResults([]); return; }
    let alive = true;
    setLinkSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await adminSearchUnlinkedPlayers(term, 10);
        if (alive) setLinkResults(r.items || []);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLinkSearching(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [linkQuery, linkDialogOpen]);

  const sportSlug = useMemo(() => sports.find((s) => s.id === form.sport_id)?.slug || '', [sports, form.sport_id]);
  const sideLabel = sportSlug === 'futbol' ? 'Pie dominante' : sportSlug === 'baloncesto' ? 'Mano dominante' : 'Lado dominante';
  const hasCatalog = positions.length > 0;
  const avatar = !isNew ? resolvePlayerAvatar(profile || { gender: form.gender }) : resolvePlayerAvatar({ gender: form.gender });

  if (loading) {
    return <AdminLayout><Skeleton className="h-64 w-full rounded-2xl" /></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="w-fit text-[var(--ns-text-muted)]">
            <Link to="/admin/fichas-jugadores" className="inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Volver al listado</Link>
          </Button>
          {form.club_id && form.team_id ? (
            <Link
              to={`/admin/clubes/${form.club_id}/equipos/${form.team_id}/directorio`}
              data-testid="admin-player-go-to-directory"
              className="inline-flex items-center gap-1 text-sm text-[var(--ns-gold-700)] hover:underline"
            >
              Directorio del equipo →
            </Link>
          ) : null}
        </div>
        <div>
          <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl text-[var(--ns-text)]">
            {isNew ? 'Crear ficha de jugador' : 'Editar ficha'}
          </h1>
          {!isNew && profile?.user ? (
            <p className="text-sm text-[var(--ns-text-muted)] mt-1">Vinculado a {profile.user.email}</p>
          ) : !isNew ? (
            <p className="text-sm text-[var(--ns-text-muted)] mt-1">Sin cuenta vinculada</p>
          ) : null}
        </div>

        {!isNew ? (
          <Card className="p-5 rounded-2xl bg-white border border-[var(--ns-border)] flex flex-col sm:flex-row gap-4 items-start">
            <div className="h-24 w-24 rounded-xl overflow-hidden bg-[var(--ns-bg-2)] flex items-center justify-center shrink-0">
              {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-[var(--ns-text-subtle)]">—</span>}
            </div>
            <div className="flex-1 flex flex-wrap gap-2 items-center">
              <label>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onUploadPhoto} />
                <span className={`inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-white border border-[var(--ns-border-strong)] text-xs cursor-pointer hover:bg-[var(--ns-amber-50)] ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Cambiar foto
                </span>
              </label>
              {profile?.profile_photo ? (
                <Button onClick={onDeletePhoto} variant="ghost" size="sm" className="h-9 text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar foto
                </Button>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 min-w-[220px]">
              {/* Control único de visibilidad pública. Escribe sobre los
                  dos campos existentes (status + external_visibility_scope)
                  usando los endpoints existentes. */}
              <div
                className="rounded-lg border border-[var(--ns-border)] bg-white p-3 flex items-start gap-3"
                data-testid="admin-player-public-visibility-card"
              >
                <div className="flex-1">
                  <Label className="text-xs font-medium block">
                    Visibilidad pública del perfil
                  </Label>
                  <p className="text-[11px] text-[var(--ns-text-subtle)] mt-1 leading-snug">
                    {isPubliclyVisible
                      ? 'Visible: cualquiera puede consultar la ficha pública.'
                      : 'Oculto: la ficha pública devuelve “no disponible”.'}
                  </p>
                </div>
                <Switch
                  checked={isPubliclyVisible}
                  disabled={publicToggleBusy || !profile}
                  onCheckedChange={handlePublicVisibilityToggle}
                  data-testid="admin-player-public-visibility-toggle"
                  aria-label="Alternar visibilidad pública del perfil"
                />
              </div>
              <Label className="text-xs font-medium mt-1">Estado</Label>
              <Select value={profile?.status} onValueChange={handleStatus}>
                <SelectTrigger data-testid="admin-player-status" className="h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Label className="text-xs font-medium mt-2">Visibilidad externa</Label>
              <Select value={profile?.external_visibility_scope} onValueChange={(v) => handleVisibility('external_visibility_scope', v)}>
                <SelectTrigger data-testid="admin-player-scope" className="h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Badge className="mt-2 text-[10px] uppercase bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] w-fit">
                {profile?.user_id ? 'Vinculada' : 'Sin vincular'}
              </Badge>
              {profile?.user_id ? (
                <Button onClick={handleUnlink} variant="outline" size="sm" className="h-9 rounded-lg text-xs mt-1">
                  <Unlink2 className="h-3.5 w-3.5 mr-1" /> Desvincular usuario
                </Button>
              ) : (
                <Button onClick={() => setLinkDialogOpen(true)} variant="outline" size="sm" data-testid="admin-player-link-manual"
                  className="h-9 rounded-lg text-xs mt-1">
                  <Link2 className="h-3.5 w-3.5 mr-1" /> Vincular manualmente
                </Button>
              )}
            </div>
          </Card>
        ) : null}

        <Card className="p-5 rounded-2xl bg-white border border-[var(--ns-border)]">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" noValidate>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Nombre *</Label>
              <Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                data-testid="admin-player-first-name" className="h-10 bg-white" required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Primer apellido *</Label>
              <Input value={form.first_surname}
                onChange={(e) => setForm((f) => ({ ...f, first_surname: e.target.value }))}
                data-testid="admin-player-first-surname" className="h-10 bg-white" required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Segundo apellido</Label>
              <Input value={form.second_surname}
                onChange={(e) => setForm((f) => ({ ...f, second_surname: e.target.value }))}
                data-testid="admin-player-second-surname" className="h-10 bg-white" />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label className="text-xs font-medium">Email (opcional)</Label>
              <Input type="email" value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Sirve para sugerir vinculación si el usuario ya está registrado."
                data-testid="admin-player-email" className="h-10 bg-white" />
              <p className="text-[11px] text-[var(--ns-text-subtle)]">
                Nunca vincula automáticamente; solo genera una sugerencia si coincide con un usuario Jugador existente.
              </p>
            </div>
            {isNew ? (
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label className="text-xs font-medium">User ID a vincular (opcional)</Label>
                <Input value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                  placeholder="Déjalo vacío si la persona no tiene cuenta aún." className="h-10 bg-white"
                  data-testid="admin-player-user-id" />
                <p className="text-[11px] text-[var(--ns-text-subtle)]">El usuario debe tener profile_type=player.</p>
              </div>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Género</Label>
              <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
                <SelectTrigger className="h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{GENDERS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Fecha de nacimiento *</Label>
              <Input type="date" required value={form.date_of_birth} onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value }))} className="h-10 bg-white" data-testid="admin-player-dob" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Altura (cm) *</Label>
              <Input type="number" required min={80} max={260} value={form.height_cm} onChange={(e) => setForm((f) => ({ ...f, height_cm: e.target.value }))} className="h-10 bg-white" data-testid="admin-player-height" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Peso (kg) *</Label>
              <Input type="number" required min={20} max={250} step="0.1" value={form.weight_kg} onChange={(e) => setForm((f) => ({ ...f, weight_kg: e.target.value }))} className="h-10 bg-white" data-testid="admin-player-weight" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Deporte *</Label>
              <Select value={form.sport_id} onValueChange={(v) => setForm((f) => ({ ...f, sport_id: v, club_id: '', team_id: '', position: '' }))}>
                <SelectTrigger data-testid="admin-player-sport" className="h-10 bg-white"><SelectValue placeholder="Selecciona deporte" /></SelectTrigger>
                <SelectContent>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Club *</Label>
              <Select value={form.club_id} onValueChange={(v) => setForm((f) => ({ ...f, club_id: v, team_id: '' }))} disabled={!form.sport_id}>
                <SelectTrigger data-testid="admin-player-club" className="h-10 bg-white"><SelectValue placeholder="Selecciona club" /></SelectTrigger>
                <SelectContent>{clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Equipo</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v }))} disabled={!form.club_id}>
                <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Selecciona equipo" /></SelectTrigger>
                <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">Posición</Label>
              {hasCatalog ? (
                <Select value={form.position} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))} disabled={!form.sport_id}>
                  <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Selecciona posición" /></SelectTrigger>
                  <SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                  placeholder="Sin posiciones definidas — escribe la tuya" disabled={!form.sport_id} className="h-10 bg-white" />
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium">{sideLabel}</Label>
              <Select value={form.dominant_side} onValueChange={(v) => setForm((f) => ({ ...f, dominant_side: v }))}>
                <SelectTrigger className="h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{SIDES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label className="text-xs font-medium">Biografía</Label>
              <Textarea value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} maxLength={600} rows={3} className="bg-white resize-none" />
            </div>
            <Button type="submit" disabled={saving} data-testid="admin-player-submit"
              className="sm:col-span-2 h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isNew ? 'Crear ficha' : 'Guardar cambios')}
            </Button>
          </form>
        </Card>

        <AlertDialog open={linkDialogOpen} onOpenChange={(o) => {
          setLinkDialogOpen(o);
          if (!o) { setLinkQuery(''); setLinkResults([]); setLinkSelectedUser(null); }
        }}>
          <AlertDialogContent data-testid="admin-player-link-dialog" className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Vincular ficha a un usuario</AlertDialogTitle>
              <AlertDialogDescription>
                Comprueba que se trata de la misma persona. La coincidencia por nombre y apellidos no garantiza que sea el mismo jugador.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="flex flex-col gap-2 mt-2">
              <Label className="text-xs font-medium">Buscar usuario</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ns-text-subtle)]" />
                <Input
                  value={linkQuery}
                  onChange={(e) => { setLinkQuery(e.target.value); setLinkSelectedUser(null); }}
                  placeholder="Escribe nombre, apellidos o email (≥ 2 caracteres)"
                  className="h-10 bg-white pl-9"
                  data-testid="admin-player-link-search"
                  autoFocus
                />
              </div>
              <p className="text-[11px] text-[var(--ns-text-muted)]">
                Solo se muestran usuarios con perfil de jugador que aún no tengan ficha vinculada.
              </p>

              <div className="max-h-60 overflow-auto rounded-lg border border-[var(--ns-border)] bg-white">
                {linkSearching ? (
                  <p className="p-3 text-xs text-[var(--ns-text-muted)] flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando...
                  </p>
                ) : linkQuery.trim().length < 2 ? (
                  <p className="p-3 text-xs text-[var(--ns-text-subtle)]">Escribe al menos 2 caracteres para empezar a buscar.</p>
                ) : linkResults.length === 0 ? (
                  <p className="p-3 text-xs text-[var(--ns-text-muted)]" data-testid="admin-player-link-empty">Sin resultados.</p>
                ) : (
                  <ul className="divide-y divide-[var(--ns-border)]">
                    {linkResults.map((u) => {
                      const isSelected = linkSelectedUser?.id === u.id;
                      return (
                        <li key={u.id}>
                          <button
                            type="button"
                            onClick={() => setLinkSelectedUser(u)}
                            data-testid={`admin-player-link-result-${u.id}`}
                            className={`w-full text-left p-3 hover:bg-[var(--ns-amber-50)] transition-colors flex items-center justify-between gap-2 ${isSelected ? 'bg-[var(--ns-amber-50)]' : ''}`}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[var(--ns-text)] truncate">
                                {u.first_name} {u.last_name}
                              </p>
                              <p className="text-xs text-[var(--ns-text-muted)] truncate">{u.email}</p>
                            </div>
                            {isSelected ? (
                              <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[10px] uppercase">Seleccionado</Badge>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLinkSubmit}
                disabled={!linkSelectedUser}
                data-testid="admin-player-link-confirm"
                className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sí, vincular
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* "Mi rendimiento" — vista privada y siempre privada (visible solo a jugador vinculado + admin) */}
        {!isNew ? <AdminPerformanceSection profileId={profile?.id} /> : null}

        {/* Zona de peligro: eliminar definitivamente la ficha */}
        {!isNew ? (
          <DangerZone
            profileFullName={fullName}
            onClickOpen={() => setDeleteStep(1)}
          />
        ) : null}

        {/* Modal 1: aviso (doble confirmación, paso 1) */}
        <AlertDialog
          open={deleteStep === 1}
          onOpenChange={(o) => { if (!o && deleteStep === 1) setDeleteStep(0); }}
        >
          <AlertDialogContent data-testid="admin-player-delete-step1">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-[var(--ns-red)]" />
                Eliminar ficha definitivamente
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  Esta acción <strong>no se puede deshacer</strong>. Se eliminará la
                  ficha por completo y, en el futuro, también todos los recursos
                  vinculados (highlights, estadísticas privadas y datos de
                  salud/biomecánica).
                </span>
                <span className="block">
                  Si solo quieres separar la cuenta de esta ficha, usa
                  &quot;Desvincular usuario&quot; en su lugar.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteStep(0)}
                className="h-10 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => setDeleteStep(2)}
                data-testid="admin-player-delete-step1-continue"
                className="h-10 rounded-xl bg-[var(--ns-red)] text-white hover:bg-[var(--ns-red)]/90"
              >
                Continuar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal 2: tecleo del nombre completo */}
        <AlertDialog
          open={deleteStep === 2}
          onOpenChange={(o) => { if (!o && deleteStep === 2) { setDeleteStep(0); setDeleteTyped(''); } }}
        >
          <AlertDialogContent data-testid="admin-player-delete-step2">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-[var(--ns-red)]">
                <ShieldAlert className="h-5 w-5" />
                Confirmar eliminación definitiva
              </AlertDialogTitle>
              <AlertDialogDescription>
                Para confirmar, escribe el nombre completo del jugador tal y como aparece:
                <span className="block mt-2 px-3 py-2 rounded bg-[var(--ns-bg-2)] font-semibold text-[var(--ns-text)] text-center select-none" data-testid="admin-player-delete-name-hint">
                  {fullName}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={deleteTyped}
              onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder="Escribe el nombre completo"
              className="h-10 bg-white mt-2"
              data-testid="admin-player-delete-typed-name"
              autoFocus
            />
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDeleteStep(0); setDeleteTyped(''); }}
                disabled={deleting}
                className="h-10 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={!canDelete || deleting}
                data-testid="admin-player-delete-confirm"
                className="h-10 rounded-xl bg-[var(--ns-red)] text-white hover:bg-[var(--ns-red)]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar definitivamente'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

function AdminPerformanceSection({ profileId }) {
  // Fase 3B (conexión Admin ↔ Player Intelligence): la API 3A ya
  // autoriza a admin/super_admin. Consumimos el último análisis existente
  // sin duplicar UI: la card "Estadísticas" abre la pantalla pública de
  // Player Intelligence ya construida (`/jugadores/{id}/analisis/{matchId}`),
  // exactamente la misma que ven jugador/público autorizado.
  const [latestId, setLatestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!profileId) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    setErrored(false);
    fetchPlayerIntelligenceLatest(profileId)
      .then((res) => { if (alive) setLatestId(res?.id || null); })
      .catch(() => { if (alive) { setLatestId(null); setErrored(true); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [profileId]);

  const onClickComing = () => toast.info('Este módulo estará disponible próximamente.');
  const hasAnalysis = !!latestId;
  const analysisHref = hasAnalysis
    ? ROUTES.playerIntelligenceMatch(profileId, latestId)
    : null;

  return (
    <Card className="p-5 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-[var(--ns-gold-700)]" />
          <p className="font-display uppercase tracking-wide text-base text-[var(--ns-text)]">
            Mi rendimiento
          </p>
        </div>
        <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] text-[10px] uppercase">Vista privada</Badge>
      </div>
      <p className="text-xs text-[var(--ns-text-muted)] mb-3">
        Visible solo para el jugador vinculado y los administradores autorizados.
        Nunca aparece en plantillas públicas, perfiles públicos ni highlights.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {loading ? (
          <div
            data-testid="admin-player-perf-stats-loading"
            className="p-4 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-amber-50)]/30 flex items-center gap-2 text-xs text-[var(--ns-text-muted)]"
          >
            <Loader2 className="h-4 w-4 animate-spin" /> Comprobando análisis…
          </div>
        ) : hasAnalysis ? (
          <AdminStatsCardActive
            testId="admin-player-perf-stats"
            href={analysisHref}
          />
        ) : (
          <AdminStatsCardEmpty
            testId="admin-player-perf-stats"
            errored={errored}
          />
        )}
        <AdminPerfCard
          icon={HeartPulse}
          title="Análisis biomecánica"
          description="Sección privada. Datos de salud y biomecánica del jugador."
          testId="admin-player-perf-health"
          onClick={onClickComing}
        />
      </div>
    </Card>
  );
}

// Card "Estadísticas" — jugador con análisis disponible. Abre la
// pantalla Player Intelligence existente (misma para admin y jugador).
function AdminStatsCardActive({ href, testId }) {
  return (
    <Link
      to={href}
      data-testid={testId}
      className="text-left flex flex-col gap-3 p-4 rounded-xl border border-[var(--ns-border-strong)] bg-white hover:bg-[var(--ns-amber-50)]/40 hover:border-[var(--ns-gold)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold)]/40"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Activity className="h-5 w-5" />
        </div>
        <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[10px] uppercase">
          Disponible
        </Badge>
      </div>
      <div>
        <p className="font-display uppercase tracking-wide text-base text-[var(--ns-text)]">
          Estadísticas
        </p>
        <p className="text-xs text-[var(--ns-text-muted)] mt-1">
          Abre el análisis completo del último partido registrado en Player Intelligence.
        </p>
      </div>
    </Link>
  );
}

// Card "Estadísticas" — jugador sin análisis todavía.
function AdminStatsCardEmpty({ testId, errored }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-3 p-4 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-bg-2)]/40"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] inline-flex items-center justify-center">
          <Activity className="h-5 w-5" />
        </div>
        <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] text-[10px] uppercase">
          Sin datos
        </Badge>
      </div>
      <div>
        <p className="font-display uppercase tracking-wide text-base text-[var(--ns-text)]">
          Estadísticas
        </p>
        <p
          className="text-xs text-[var(--ns-text-muted)] mt-1"
          data-testid="admin-player-perf-stats-empty-msg"
        >
          {errored
            ? 'No se pudo comprobar Player Intelligence en este momento.'
            : 'Todavía no hay análisis disponibles.'}
        </p>
      </div>
    </div>
  );
}

function AdminPerfCard({ icon: Icon, title, description, testId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="text-left flex flex-col gap-3 p-4 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-amber-50)]/30 hover:bg-[var(--ns-amber-50)]/60 hover:border-[var(--ns-gold)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold)]/40"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[10px] uppercase">Próximamente</Badge>
      </div>
      <div>
        <p className="font-display uppercase tracking-wide text-base text-[var(--ns-text)]">{title}</p>
        <p className="text-xs text-[var(--ns-text-muted)] mt-1">{description}</p>
      </div>
    </button>
  );
}

function DangerZone({ profileFullName, onClickOpen }) {
  return (
    <Card
      data-testid="admin-player-danger-zone"
      className="p-5 rounded-2xl bg-white border border-[var(--ns-red)]/40"
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-[var(--ns-red)]" />
        <p className="text-sm font-semibold text-[var(--ns-red)] uppercase tracking-wide">
          Zona de peligro
        </p>
      </div>
      <p className="text-xs text-[var(--ns-text-muted)] mb-4">
        Eliminar definitivamente la ficha de <strong>{profileFullName || 'este jugador'}</strong>.
        Esto borrará la ficha y sus sugerencias asociadas. En el futuro también
        eliminará highlights, estadísticas privadas y datos de salud/biomecánica.
      </p>
      <Button
        type="button"
        onClick={onClickOpen}
        data-testid="admin-player-delete-open"
        className="h-10 rounded-xl bg-white border border-[var(--ns-red)] text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8"
      >
        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
        Eliminar ficha definitivamente
      </Button>
    </Card>
  );
}
